import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useSuperAdmin } from './SuperAdminContext';

const SupportNotificationsContext = createContext(null);

const POLL_INTERVAL_MS = 60_000; // 60s background heartbeat. Focus events refresh instantly.

/**
 * Shared state shape for support-ticket notifications:
 *   - unreadCount: number of OPEN tickets with unread activity
 *   - unreadTickets: up to 3 most-recently-updated unread tickets (preview data)
 *   - refresh(): re-fetch on demand (called on focus, after marking read, etc.)
 *   - markRead(ticketId): mark a single ticket as read & optimistically clear the badge
 *
 * Two providers below — one for the tenant tree, one for the super admin tree —
 * both expose the SAME `useSupportNotifications()` hook so callers (Layout,
 * Dashboard, TicketDetail, etc.) are role-agnostic.
 */
function useSupportNotificationsPoller({ api, active, summaryUrl, markReadUrl }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState([]);
  const pollTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!active) return;
    try {
      const res = await api.get(summaryUrl);
      setUnreadCount(res.data?.unread_count || 0);
      setUnreadTickets(res.data?.tickets || []);
    } catch (err) {
      // Silent — endpoint may 401 mid-auth-refresh; next poll will recover
    }
  }, [api, summaryUrl, active]);

  const markRead = useCallback(async (ticketId) => {
    if (!active) return;
    try {
      await api.post(markReadUrl(ticketId));
      setUnreadTickets((prev) => prev.filter((t) => t.id !== ticketId));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      refresh(); // resync in background
    } catch (err) {
      // Ignore — refresh picks up truth
    }
  }, [api, markReadUrl, refresh, active]);

  useEffect(() => {
    if (!active) {
      setUnreadCount(0);
      setUnreadTickets([]);
      return undefined;
    }
    refresh();
    pollTimerRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(pollTimerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh, active]);

  return { unreadCount, unreadTickets, refresh, markRead };
}

export const SupportNotificationsProvider = ({ children }) => {
  const { api, user } = useAuth();
  const state = useSupportNotificationsPoller({
    api,
    active: !!user,
    // Tenant `api` already has `/api` baked into baseURL, so URLs here are
    // relative to that — do NOT re-prefix `/api`.
    summaryUrl: '/support/tickets/unread-summary',
    markReadUrl: (id) => `/support/tickets/${id}/mark-read`,
  });

  // Tenant-wide dispatcher notifications (e.g. worker confirmations, route stops)
  const [general, setGeneral] = useState({ unread_count: 0, items: [] });
  const genTimerRef = useRef(null);
  const knownIdsRef = useRef(new Set());
  const initialLoadDoneRef = useRef(false);

  const refreshGeneral = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/dispatcher-notifications/unread-summary');
      const items = res.data?.items || [];
      const count = res.data?.unread_count || 0;

      // Detect genuinely new items (not on first load — only subsequent polls)
      if (initialLoadDoneRef.current) {
        for (const item of items) {
          if (item.id && !knownIdsRef.current.has(item.id)) {
            // Fire a toast for the new notification
            try {
              const { toast } = await import('sonner');
              toast(item.title || 'New notification', {
                description: item.message || '',
                duration: 6000,
              });
            } catch (_) { /* sonner may not be ready */ }
          }
        }
      }
      // Update known IDs
      knownIdsRef.current = new Set(items.map(n => n.id));
      initialLoadDoneRef.current = true;

      setGeneral({ unread_count: count, items });
    } catch (err) {
      // Silent — 401 mid-refresh or missing permission; next poll recovers
    }
  }, [api, user]);

  const markGeneralRead = useCallback(async (id) => {
    if (!user) return;
    try {
      await api.post(`/dispatcher-notifications/${id}/mark-read`);
      setGeneral((prev) => ({
        unread_count: Math.max(0, prev.unread_count - 1),
        items: prev.items.filter((n) => n.id !== id),
      }));
      refreshGeneral();
    } catch (err) {
      // Ignore — refresh picks up truth
    }
  }, [api, user, refreshGeneral]);

  useEffect(() => {
    if (!user) {
      setGeneral({ unread_count: 0, items: [] });
      return undefined;
    }
    refreshGeneral();
    genTimerRef.current = setInterval(refreshGeneral, POLL_INTERVAL_MS);
    const onFocus = () => refreshGeneral();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(genTimerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshGeneral, user]);

  const merged = {
    ...state,
    // Total badge count (support tickets + dispatcher notifications) for the top-bar bell
    unreadCount: state.unreadCount + general.unread_count,
    // Support-ticket-only count for the Dashboard widget
    supportTicketUnreadCount: state.unreadCount,
    generalNotifications: general.items,
    generalUnreadCount: general.unread_count,
    markGeneralRead,
  };

  return (
    <SupportNotificationsContext.Provider value={merged}>
      {children}
    </SupportNotificationsContext.Provider>
  );
};

export const SuperAdminSupportNotificationsProvider = ({ children }) => {
  const { api, admin } = useSuperAdmin();
  const state = useSupportNotificationsPoller({
    api,
    active: !!admin,
    summaryUrl: '/api/super-admin/tickets/unread-summary',
    markReadUrl: (id) => `/api/super-admin/tickets/${id}/mark-read`,
  });
  return (
    <SupportNotificationsContext.Provider value={state}>
      {children}
    </SupportNotificationsContext.Provider>
  );
};

export const useSupportNotifications = () => {
  const ctx = useContext(SupportNotificationsContext);
  if (!ctx) {
    return {
      unreadCount: 0,
      supportTicketUnreadCount: 0,
      unreadTickets: [],
      refresh: () => {},
      markRead: () => {},
      generalNotifications: [],
      generalUnreadCount: 0,
      markGeneralRead: () => {},
    };
  }
  return ctx;
};
